import { Link } from '@tanstack/react-router'
import * as React from 'react'

import { Card, CardContent } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { StatusDot } from '@/components/ui/status-dot'
import { cn } from '@/lib/utils'

export interface DashboardStatusCardProps {
  operational: number
  needsAttention: number
  offline: number
  className?: string
}

const statusRows = [
  {
    variant: 'success' as const,
    key: 'operational' as const,
    label: (n: number) => `${n === 1 ? 'Machine' : 'Machines'} operational`,
    statusFilter: 'operational',
  },
  {
    variant: 'warning' as const,
    key: 'needsAttention' as const,
    label: (n: number) => `${n === 1 ? 'Machine needs' : 'Machines need'} attention`,
    statusFilter: 'needs-attention',
  },
  {
    variant: 'destructive' as const,
    key: 'offline' as const,
    label: (n: number) => `${n === 1 ? 'Machine' : 'Machines'} offline`,
    statusFilter: 'offline',
  },
]

export function DashboardStatusCard({
  operational,
  needsAttention,
  offline,
  className,
}: DashboardStatusCardProps): React.ReactElement {
  const counts = { operational, needsAttention, offline }

  return (
    <Card className={cn('border-0 p-7 shadow-lg', className)}>
      <CardContent className="flex h-full flex-col gap-5 p-0">
        <h2 className="text-3xl font-extrabold tracking-tight">Status</h2>
        <div className="my-auto flex flex-col gap-4">
          {statusRows.map((row, index) => (
            <React.Fragment key={row.key}>
              {index > 0 && <Separator />}
              <Link
                to="/machines"
                search={{ status: row.statusFilter }}
                className="flex items-center gap-2.5 opacity-80 transition-opacity hover:opacity-100"
              >
                <StatusDot variant={row.variant} className="size-4 [&>div]:size-2.5" />
                <span className="w-5 text-sm font-semibold tabular-nums">{counts[row.key]}</span>
                <span className="text-sm">{row.label(counts[row.key])}</span>
              </Link>
            </React.Fragment>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
